'use client'

import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage, RESET } from 'jotai/utils'

import { compressAndZipChartConfig } from '@/features/chart/utils/chart-config-compression'
import {
  ALL_NEWS_SOURCES,
  NEWS_SOURCE_FILTER_VERSION,
  type NewsSourceId,
} from '@/features/dashboard/components/widgets/news-row'
import type { ActivityKind } from '@/features/dashboard/lib/activity-item'
import type { ComponentKey } from '@/features/dashboard/lib/widget-sizes'
import type { DeltaBucket } from '@/features/data/components/widgets/content/amberdata/delta-surface-chart'
import type { DeltaKey } from '@/features/data/components/widgets/content/amberdata/skew-chart'
import type { EtfFlowDenomination } from '@/features/data/components/widgets/content/etf/etf-flow-normalize'
import type { ImpactBucket } from '@/lib/coinmarketcal-merge'
import { DEFAULT_VENUE_EXCHANGE } from '@/lib/configs/default-venue'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import store from '@/lib/configs/store-config'
import { safeStorage } from '@/lib/safe-storage'
import type { QuantityDisplay } from '@/lib/utils/format-quantity'
import {
  PHASE_ONE_ANALYTICS_TYPES,
  type PhaseOneAmberdataAnalyticsType,
} from '@/service/amberdata/analytics.types'
import { type EtfFlowAsset, type EtfFlowMetric, type EtfFlowView } from '@/service/etf-flow.types'
import { type FedwatchView } from '@/service/fedwatch.types'
import {
  OPTIONS_OI_SERIES_DEFAULT_LIMIT,
  OPTIONS_OI_SERIES_MIN_TIMEFRAME_SECONDS,
} from '@/service/options-analytics.types'
import { chartSelectedAccountIdAtomFamily } from '@/state/chart-account'
import { type LinkingColor, linkingColorsAtomFamily } from '@/state/linking'
import {
  chartWidgetScopeKey,
  cleanupUnlinkedOptionRows,
  DEFAULT_GROUP_STATE,
  linkingGroupStateAtomFamily,
  resolveLegacyChartOption,
  resolveLegacyTabTimeframe,
  unlinkedCardStateAtomFamily,
} from '@/state/linking-group'
import {
  riskSimulationResponseAtomFamily,
  riskSimulationTriggerAtomFamily,
  riskSlidesFilterAtomFamily,
  savedRiskScenariosAtomFamily,
  simulatedPortfolioAtomFamily,
} from '@/state/risk'
import { type ChartOption, normalizePositionsTab, type PositionsTabValue } from '@/types/dashboard'
import { AnalyticsType, ChartTimeframe, Exchange, OptionType } from '@/types/generated/common/types'
import {
  BlockTradeMode,
  OptionsOiRankMode,
  OptionsVolumeGroupBy,
  OptionsVolumeMeasure,
} from '@/types/generated/public_api'

import { PositionTableTab } from './dashboard'

export { chartSelectedAccountIdAtomFamily } from '@/state/chart-account'

/**
 * Per-widget row selection state for position tables
 * Each widget instance maintains its own row selection
 * Key: instanceId (e.g., 'positions-1'), Value: Record<rowId, isSelected>
 */
export const selectedPositionsByInstanceAtom = atomFamily((instanceId: string) =>
  atom<Record<string, boolean>>({})
)

/**
 * Per-widget position table tab state
 * Each position table instance can be on different tabs independently
 * Key: instanceId (e.g., 'positions-1'), Value: PositionsTabValue — either a
 * real PositionTableTab or (while History is "expanded", see
 * positionsHistoryExpandedByInstanceAtom below) one of its section tabs.
 */
export const positionTableTabByInstanceAtom = atomFamily((instanceId: string) =>
  atom<PositionsTabValue>(PositionTableTab.POSITIONS)
)

/**
 * Per-widget "History tab sections as top-level tabs" mode. Persisted to the
 * server widget config (`historyTabsExpanded`, see usePositionsConfigSync /
 * restorePositions); the selected tab itself stays in-memory only, same as
 * positionTableTabByInstanceAtom always has.
 */
export const positionsHistoryExpandedByInstanceAtom = atomFamily((_instanceId: string) =>
  atom(false)
)

/**
 * Flips true once `restorePositions` has applied (or found nothing to apply
 * for) this widget's server config. usePositionsConfigSync gates its
 * historyTabsExpanded sync on this — restore's `store.set` can land AFTER
 * the widget's own first-render effect has already captured its "previous
 * value" baseline (restore runs once an async layout fetch resolves, not
 * synchronously on mount), so without this gate a `false → true` restore
 * would look like a user change and get written straight back to the
 * server. Set to true for a freshly duplicated widget too, since
 * duplicatePositions already copied the value synchronously — there's no
 * incoming restore left to race against for that instance until its own
 * later layout fetch.
 */
export const positionsRestoredAtomFamily = atomFamily((_instanceId: string) => atom(false))

/**
 * The Positions widget's Active / History kind filter (All, Orders, TWAP, …), keyed
 * `${instanceId}:${mode}`. Lives outside ActivityView because that unmounts whenever its tab
 * isn't showing — local state there reset to All on every tab switch. In-memory only, like the
 * selected tab itself (positionTableTabByInstanceAtom).
 */
export const activityKindFilterAtomFamily = atomFamily((_key: string) =>
  atom<ActivityKind | 'all'>('all')
)

/** Count-only bridge for the Positions widget's Active tab badge. */
export const activeRowCountByInstanceAtom = atomFamily((_instanceId: string) => atom(0))

/** Count-only bridge for the Positions tab after its widget-scoped filters are applied. */
export const filteredPositionCountByInstanceAtom = atomFamily((_instanceId: string) =>
  atom<number | null>(null)
)

/**
 * Per-widget quantity display format ('normal' | 'abbr')
 * Used by order-book and trades widgets.
 * Default: 'normal'
 */
export const quantityDisplayAtomFamily = atomFamily((widgetId: string) =>
  atom<QuantityDisplay>('normal')
)

export type OrderBookDecimalDisplay = 'auto' | 'show' | 'hide'

export const contractMultiplierModeAtomFamily = atomFamily((_widgetId: string) =>
  atom<'default' | 'adjust'>('default')
)

export const orderbookDecimalDisplayAtomFamily = atomFamily((widgetId: string) =>
  atom<OrderBookDecimalDisplay>('auto')
)

/**
 * Per-widget persisted settings for the recent-trades widget.
 * Stored as enum values (not ticker strings) so they survive tab/symbol changes.
 */
export const tradesAmountDenomAtomFamily = atomFamily((widgetId: string) =>
  atom<'base' | 'quote'>('quote')
)

export const tradesSideFilterAtomFamily = atomFamily((widgetId: string) =>
  atom<'all' | 'buy' | 'sell'>('all')
)

export const tradesTimeDisplayAtomFamily = atomFamily((widgetId: string) =>
  atom<'normal' | 'relative'>('normal')
)

export const tradesAggregationSecondsAtomFamily = atomFamily((widgetId: string) => atom<number>(0))

export const tradesAggregationPriceModeAtomFamily = atomFamily((widgetId: string) =>
  atom<'last' | 'vwap'>('last')
)

export const tradesBlockFilterAtomFamily = atomFamily((widgetId: string) =>
  atom<'all' | 'block' | 'regular'>('all')
)

export const tradesMaxCountAtomFamily = atomFamily((widgetId: string) => atom<100 | 200 | 300>(100))

export const tradesRollingWindowAtomFamily = atomFamily((widgetId: string) =>
  atom<5 | 15 | 30 | 60>(5)
)

export const orderbookMaxRowsAtomFamily = atomFamily((widgetId: string) =>
  atom<20 | 50 | 100 | 200 | 500>(20)
)

/**
 * Per-widget orderbook display toggles
 */
export const orderbookShowBookPressureAtomFamily = atomFamily((widgetId: string) =>
  atom<boolean>(true)
)

export const orderbookShowVolumeDeltaAtomFamily = atomFamily((widgetId: string) =>
  atom<boolean>(false)
)

export const orderbookShowGaugesAtomFamily = atomFamily((widgetId: string) => atom<boolean>(false))

export const orderbookShowFooterStatsAtomFamily = atomFamily((widgetId: string) =>
  atom<boolean>(false)
)

/** Backward-compatible persisted value for Account Cards' "all accounts" scope. */
export const ALL_ACCOUNTS_ID = 'ALL'

/**
 * Account scope selected by each Account Cards widget. `null` aggregates every active
 * account; an array represents an explicit subset (including an intentionally empty one).
 * Persistence accepts the old bare-account-id format and writes arrays as JSON.
 */
export const accountCardsSelectedAccountIdAtomFamily = atomFamily((_widgetId: string) =>
  atom<string[] | null>(null)
)

/**
 * Per-widget drawing panel (left sidebar) visibility for price-chart widgets.
 * Persisted to server config so it survives page refresh.
 * Default: true (visible)
 */
export const drawingPanelVisibleAtomFamily = atomFamily((widgetId: string) => atom<boolean>(true))

/**
 * Per-widget option-chain-mode visibility for price-chart widgets (merged option chain).
 * Ephemeral session-only UI state: not persisted to server config, not copied on
 * widget duplication/clone. Default: true (Option Chain when the active symbol supports it).
 */
export const optionChainViewVisibleAtomFamily = atomFamily((widgetId: string) =>
  atom<boolean>(true)
)

/**
 * Per-widget memory of the chart/option-chain toggle, keyed by option ticker family
 * (e.g. "ETH", "BTC") so returning to a previously-viewed underlying — via the widget's
 * own tab switch, dashboard page navigation, or a reload — restores whatever chain
 * visibility the user last chose for THAT family, instead of always falling back to the
 * type-based default. Persisted (survives reload), unlike optionChainViewVisibleAtomFamily
 * itself. A family key absent from the map means "never explicitly chosen" — callers
 * should fall back to the type-based default in that case.
 */
export const optionChainVisiblePreferenceAtomFamily = atomFamily((widgetId: string) =>
  atomWithStorage<Record<string, boolean>>(
    `${STORAGE_KEYS.dashboard.chartOptionChainVisiblePreference}-${widgetId}`,
    {},
    safeStorage,
    { getOnInit: true }
  )
)

/**
 * Per-chart-widget settings (chart type, scale/autoScale, drawings/indicators
 * via `content`, overlay toggles). Keyed by `chartWidgetScopeKey(pageId, cardId)`
 * — NOT raw cardId, since legacy widget IDs are only unique per page.
 *
 * `undefined` (the atom's initial value) means "never individually set" and
 * signals callers to fall back to `resolveLegacyChartOption` — the value this
 * widget used to read back when chart settings were shared per linking color.
 * The first write forks the widget onto its own storage permanently; there is
 * no separate migration step.
 */
export const chartOptionAtomFamily = atomFamily((scopeKey: string) =>
  atomWithStorage<ChartOption | undefined>(
    `${STORAGE_KEYS.dashboard.chartOption}-${scopeKey}`,
    undefined,
    safeStorage,
    { getOnInit: true }
  )
)

export interface ChartTabTimeframeState {
  /**
   * Last timeframe set on any tab in this widget. Also what server restore
   * seeds (the server only ever stores one scalar `timeframe` per widget) —
   * there's no reliable "focused tab" to attach a restored value to on a
   * fresh device with an empty, purely-local tab list.
   */
  default?: number
  /** Overrides `default` once a specific tab's timeframe is explicitly changed. */
  perTab: Record<string, number>
}

/**
 * Per-chart-widget tab timeframes. Same scoping/lazy-fallback rules as
 * chartOptionAtomFamily — see resolveLegacyTabTimeframe.
 */
export const chartTabTimeframeAtomFamily = atomFamily((scopeKey: string) =>
  atomWithStorage<ChartTabTimeframeState | undefined>(
    `${STORAGE_KEYS.dashboard.chartTabTimeframe}-${scopeKey}`,
    undefined,
    safeStorage,
    { getOnInit: true }
  )
)

export interface CalendarWidgetConfig {
  impactFilter: string
  areaFilter: string
  view: 'monthly' | 'daily'
  year?: number
  month?: number
  dayDateMs?: number
  // Which calendar this widget instance is currently showing — the
  // macro-economic month/day grid, or the CoinMarketCal event list
  // (ENG-870). Defaults to 'economic' so every already-saved
  // economic-calendar widget instance keeps rendering exactly as before.
  source: 'economic' | 'coinmarketcal'
  // CoinMarketCal filters. `null` means "All categories" / "no coin filter",
  // same convention the removed Data Widget settings used.
  coinMarketCalCategory: string | null
  coinMarketCalCoin: string | null
  // UTC day starts. `null` keeps the feed's rolling defaults: today for
  // `from`, and no upper bound for `to`.
  coinMarketCalFromDateMs: number | null
  coinMarketCalToDateMs: number | null
  // Client-side only (no server-side impact filter exists for this feed) —
  // same 'All' sentinel convention as the macro-economic `impactFilter` above.
  coinMarketCalImpactFilter: 'All' | ImpactBucket
}

export const CALENDAR_CONFIG_DEFAULT: CalendarWidgetConfig = {
  impactFilter: 'high',
  areaFilter: 'All',
  view: 'monthly',
  source: 'economic',
  coinMarketCalCategory: null,
  coinMarketCalCoin: null,
  coinMarketCalFromDateMs: null,
  coinMarketCalToDateMs: null,
  coinMarketCalImpactFilter: 'All',
}

export const calendarConfigAtomFamily = atomFamily((widgetId: string) =>
  atom<CalendarWidgetConfig>(CALENDAR_CONFIG_DEFAULT)
)

export const watchlistActiveIdAtomFamily = atomFamily((widgetId: string) =>
  atom<string | null>(null)
)

export interface WatchlistColumnVisibility {
  exchange: boolean
  price: boolean
  change: boolean
  bid: boolean
  ask: boolean
}

// Single source of truth for defaults — reused by the atomFamily initializer AND the
// restore-from-server fallback in widget-config-restore.ts, so they can't drift apart.
export const WATCHLIST_COLUMN_VISIBILITY_DEFAULT: WatchlistColumnVisibility = {
  exchange: false,
  price: true,
  change: true,
  bid: true,
  ask: true,
}

export const watchlistColumnVisibilityAtomFamily = atomFamily((widgetId: string) =>
  atom<WatchlistColumnVisibility>(WATCHLIST_COLUMN_VISIBILITY_DEFAULT)
)

export const watchlistActiveIdReadyAtomFamily = atomFamily((widgetId: string) =>
  atom<boolean>(false)
)

export type DataWidgetRangeDays = '1' | '7' | '30' | '90'

/**
 * Data Widget content selection. The non-Amberdata entries — Option Volume/Open
 * Interest/Trades/Strike Flow (ENG-?) and ETF Flow (ENG-868) — live on different
 * backend surfaces than the AnalyticsType-keyed Amberdata entries, so they're
 * modeled as a discriminated union rather than a second independent atom — that
 * keeps an invalid {kind, analyticsType} combination or stale cross-atom state from
 * being representable. Those branches carry `previousAnalyticsType` so switching
 * into one and back returns to the same Amberdata selection instead of resetting to
 * the first entry.
 */
export type DataWidgetContentSelection =
  | { kind: 'amberdata'; analyticsType: PhaseOneAmberdataAnalyticsType }
  | {
      kind:
        | 'optionVolume'
        | 'optionOpenInterest'
        | 'optionTrades'
        | 'optionStrikeFlow'
        | 'optionStructure'
        | 'ivOverTime'
        | 'etfFlow'
        | 'fedwatch'
        | 'perpPositions'
      previousAnalyticsType: PhaseOneAmberdataAnalyticsType
    }

export const DATA_WIDGET_CONTENT_DEFAULT: DataWidgetContentSelection = {
  kind: 'amberdata',
  analyticsType: PHASE_ONE_ANALYTICS_TYPES[0],
}

export const dataWidgetContentAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetContentSelection>(DATA_WIDGET_CONTENT_DEFAULT)
)

export const dataWidgetRangeDaysAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetRangeDays>('30')
)

// ms timestamp for "date 1" in comparison types (null = current snapshot)
export const dataWidgetDate1AtomFamily = atomFamily((widgetId: string) => atom<number | null>(null))

// ms timestamp for "date 2" comparison (null = disabled / no comparison)
export const dataWidgetDate2AtomFamily = atomFamily((widgetId: string) => atom<number | null>(null))

// from date for IvR date-range pickers (null = use 7-day default)
export const dataWidgetFromDateAtomFamily = atomFamily((widgetId: string) =>
  atom<number | null>(null)
)

// to date for IvR date-range pickers (null = now)
export const dataWidgetToDateAtomFamily = atomFamily((widgetId: string) =>
  atom<number | null>(null)
)

// which axis the comparison analytics types compare against
export const dataWidgetCompareModeAtomFamily = atomFamily((widgetId: string) =>
  atom<'date' | 'asset'>('date')
)

// second underlying symbol for asset-vs-asset comparison (null = disabled)
export const dataWidgetCompareSymbolAtomFamily = atomFamily((widgetId: string) =>
  atom<string | null>(null)
)

// Which delta bucket Skew (Constant/Floating) plots — lives in the widget's main
// controls row, not inside the chart itself. Default mirrors skew-chart.tsx's own
// DEFAULT_DELTA ('skew25' — the "backbone" delta); only the type is imported from
// there (type-only, erased at compile time) to avoid pulling the chart component
// (and its lightweight-charts dependency) into this shared state module.
export const dataWidgetSkewDeltaAtomFamily = atomFamily((widgetId: string) =>
  atom<DeltaKey>('skew25')
)

// Delta Surface (Constant/Floating) render mode — 3D surface (default) or the
// original 2D per-expiry line chart. Lives in the widget's main controls row.
export type DeltaSurfaceViewMode = '2d' | '3d'
export const dataWidgetDeltaSurfaceViewModeAtomFamily = atomFamily((widgetId: string) =>
  atom<DeltaSurfaceViewMode>('3d')
)

// "IV over Time" entry — plots one delta bucket's IV across a picked date range
// (see iv-over-time-content.tsx). `analyticsType` reuses
// the real Delta Surface Constant/Floating AnalyticsType (there's no separate
// backend type for this — it's the same data, viewed differently), narrowed to
// just those two values so it can be passed straight into
// buildAmberdataAnalyticsRequest. `delta` type is imported type-only from
// delta-surface-chart.tsx (erased at compile time, no bundle cost here).
export type DataWidgetIvOverTimeAnalyticsType =
  | AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT
  | AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_FLOATING

export interface DataWidgetIvOverTimeSettings {
  analyticsType: DataWidgetIvOverTimeAnalyticsType
  delta: DeltaBucket
  fromDateMs: number | null
  toDateMs: number | null
}

export const DATA_WIDGET_IV_OVER_TIME_SETTINGS_DEFAULT: DataWidgetIvOverTimeSettings = {
  analyticsType: AnalyticsType.ANALYTICS_TYPE_DELTA_SURFACE_CONSTANT,
  delta: 'atm',
  fromDateMs: null,
  toDateMs: null,
}

export const dataWidgetIvOverTimeSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetIvOverTimeSettings>(DATA_WIDGET_IV_OVER_TIME_SETTINGS_DEFAULT)
)

// "ETF Flow" entry (ENG-868) — daily crypto-ETF fund flows + AUM. Both fields live
// in ONE settings object rather than two atoms because `syncConfig` is debounced per
// widget: two separate writes in the same tick would see the second cancel the first.
export interface DataWidgetEtfFlowSettings {
  asset: EtfFlowAsset
  /**
   * Explicit UTC-midnight bounds. `null` means "never picked" and resolves to the
   * default window at render — storing a concrete date on mount would freeze the
   * widget to whatever day it was first opened.
   */
  fromDateMs: number | null
  toDateMs: number | null
  /** USD as published, or divided through by each day's price. Chart view only —
   * the by-fund table always renders USD (see EtfFlowTableView). */
  denomination: EtfFlowDenomination
  /** Chart is the aggregate series; table is the by_fund per-fund breakdown. */
  view: EtfFlowView
  /** Which field the table renders. Ignored in chart view, which shows both. */
  metric: EtfFlowMetric
}

export const DATA_WIDGET_ETF_FLOW_SETTINGS_DEFAULT: DataWidgetEtfFlowSettings = {
  asset: 'BTC',
  fromDateMs: null,
  toDateMs: null,
  denomination: 'usd',
  view: 'chart',
  metric: 'fundFlow',
}

export const dataWidgetEtfFlowSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetEtfFlowSettings>(DATA_WIDGET_ETF_FLOW_SETTINGS_DEFAULT)
)

// "FedWatch" entry (ENG-851) — CME target-rate probabilities off the ZQ strip.
// `meetingDate` is only meaningful for the timeline view; '' means "the nearest
// meeting", which is also what a persisted date resolves to once that meeting has
// passed and the strip has moved on.
export interface DataWidgetFedwatchSettings {
  view: FedwatchView
  meetingDate: string
  /** Timeline bounds as UTC days. `null` = never picked, resolved at render. */
  fromDateMs: number | null
  toDateMs: number | null
}

export const DATA_WIDGET_FEDWATCH_SETTINGS_DEFAULT: DataWidgetFedwatchSettings = {
  view: 'grid',
  meetingDate: '',
  fromDateMs: null,
  toDateMs: null,
}

export const dataWidgetFedwatchSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetFedwatchSettings>(DATA_WIDGET_FEDWATCH_SETTINGS_DEFAULT)
)

export const newsSearchQueryAtomFamily = atomFamily((widgetId: string) => atom<string>(''))

// Currently active/selected category tab — 'all' means no newsType filter applied.
export const newsCategoryFilterAtomFamily = atomFamily((widgetId: string) => atom<string>('all'))

// User-configured set of newsType values pinned as tabs (via the settings checklist).
// 'all' is implicit and never stored here — it's always rendered first and unremovable.
export const newsCategoryTabsAtomFamily = atomFamily((widgetId: string) => atom<string[]>([]))

export const newsTimestampModeAtomFamily = atomFamily((widgetId: string) =>
  atom<'relative' | 'absolute'>('relative')
)

export interface NewsColumnVisibility {
  url: boolean
  timestamp: boolean
}

export const newsColumnVisibilityAtomFamily = atomFamily((widgetId: string) =>
  atom<NewsColumnVisibility>({ url: true, timestamp: true })
)

export type NewsTagFilter = 'all' | 'HOT' | 'WARM'

export const newsTagFilterAtomFamily = atomFamily((widgetId: string) => atom<NewsTagFilter>('all'))

export type NewsMacroEventFilter = 'all' | 'macro'

export const newsMacroEventFilterAtomFamily = atomFamily((widgetId: string) =>
  atom<NewsMacroEventFilter>('all')
)

// Which news sources are merged into the feed. All sources are on by default.
export const newsSourceFilterAtomFamily = atomFamily((widgetId: string) =>
  atom<NewsSourceId[]>([...ALL_NEWS_SOURCES])
)

/**
 * Per-widget One Tap Buy button config — a small fixed list of preset
 * side/amount pairs the user taps to fire an immediate market order.
 * Direction lives in `side`, not encoded into the amount's sign. `amount` is
 * denominated in either the quote currency (USD notional) or the base
 * currency (coin size), per `denomination`. `contracts` is the button's size
 * when the widget is on an option, which is always sized in contracts and
 * never in quote (see features/quick-order/lib/option-sizing.ts).
 */
export type OneTapBuySide = 'buy' | 'sell'
export type OneTapBuyDenomination = 'base' | 'quote'

export interface OneTapBuyButtonConfig {
  side: OneTapBuySide
  amount: string
  denomination: OneTapBuyDenomination
  contracts?: string
}

export const ONE_TAP_BUY_MIN_BUTTONS = 1
export const ONE_TAP_BUY_MAX_BUTTONS = 6

export const ONE_TAP_BUY_DEFAULT_BUTTONS: OneTapBuyButtonConfig[] = [
  { side: 'buy', amount: '100', denomination: 'quote', contracts: '1' },
  { side: 'buy', amount: '500', denomination: 'quote', contracts: '5' },
  { side: 'buy', amount: '1000', denomination: 'quote', contracts: '10' },
]

export const oneTapBuyButtonsAtomFamily = atomFamily((widgetId: string) =>
  atom<OneTapBuyButtonConfig[]>(ONE_TAP_BUY_DEFAULT_BUTTONS)
)

/**
 * Per-tab Spreader matrix instrument lists — the row (left) and column (top) instruments
 * the user has added to that tab's spread matrix.
 */
export interface SpreaderMatrixInstrument {
  exchange: Exchange
  symbol: string
}

// Spreader's "tabs" are NOT an independent list — they ARE the widget's shared
// linking-group watchlist (the same tabs Order Book/Trades/Chart show), so switching
// tabs anywhere in the group switches them everywhere. This is purely the per-tab
// matrix data, keyed by that shared watchlist item's `id` (state/storage.ts's
// WatchList.list[].id, stable across reorders — unlike array index). `seeded` lives
// inside each entry (not a separate flag/key) so restoring a tab's exact prior state —
// including whether it was ever auto-seeded, even if the user then emptied it — is a
// single JSON round-trip with no separate "is this legacy or intentionally-emptied"
// reconciliation needed at restore time. An entry whose watchlist item was since
// removed (by this widget or another one in the same linked group) is a harmless
// orphan — never proactively cleaned up, same as other per-tab caches in this file.
export interface SpreaderTabMatrixState {
  rows: SpreaderMatrixInstrument[]
  columns: SpreaderMatrixInstrument[]
  seeded: boolean
}

export interface SpreaderWidgetState {
  byTabId: Record<string, SpreaderTabMatrixState>
}

export const spreaderStateAtomFamily = atomFamily((widgetId: string) =>
  atom<SpreaderWidgetState>({ byTabId: {} })
)

// Runtime-only (never persisted, never duplicated) — true once restoreSpreader has
// evaluated this widget's server config at least once this session. The seed effect in
// spreader-matrix-widget.tsx gates on this so it never races ahead of restore and
// stomps a real saved config with a fresh auto-seed.
export const spreaderRestoredAtomFamily = atomFamily((widgetId: string) => atom(false))

// OPTIONS_ANALYTICS_DEFAULT_MEASURE/TIMEFRAME are shared defaults for every option
// content entry in the Data Widget (see dataWidgetOption*SettingsAtomFamily below) —
// named for the retired standalone Options Analysis widget they originated from, kept
// as-is since renaming would touch every settings default in this section.
export const OPTIONS_ANALYTICS_DEFAULT_MEASURE =
  OptionsVolumeMeasure.OPTIONS_VOLUME_MEASURE_NOTIONAL_BASE
export const OPTIONS_ANALYTICS_DEFAULT_TIMEFRAME: number = ChartTimeframe.CHART_TIMEFRAME_DAY_1

export interface OptionsAnalyticsTradesSettings {
  optionType: OptionType
  blockMode: BlockTradeMode
  sizeMeasure: OptionsVolumeMeasure
  minSizeInput: string
}

export const OPTIONS_ANALYTICS_TRADES_SETTINGS_DEFAULT: OptionsAnalyticsTradesSettings = {
  optionType: OptionType.OPTION_TYPE_UNSPECIFIED,
  blockMode: BlockTradeMode.BLOCK_TRADE_MODE_ALL,
  sizeMeasure: OPTIONS_ANALYTICS_DEFAULT_MEASURE,
  minSizeInput: '',
}

/**
 * Per-widget local-control state for the Data Widget's Option Volume/Open
 * Interest/Trades entries (see `DataWidgetContentSelection` above).
 *
 * Exchange/underlying are shared across every option entry (switching from Option
 * Volume to Option Open Interest keeps the same exchange/underlying selection)
 * rather than living on each settings object individually. Default exchange is
 * DEFAULT_VENUE_EXCHANGE (Deribit); `underlying` defaults to `null`, meaning "not yet set — seed from the
 * Data Widget's active symbol," resolved by a correction effect in the consuming
 * component (the atom can't know the symbol at creation time).
 */
export const dataWidgetOptionExchangeAtomFamily = atomFamily((widgetId: string) =>
  atom<Exchange>(DEFAULT_VENUE_EXCHANGE)
)

export const dataWidgetOptionUnderlyingAtomFamily = atomFamily((widgetId: string) =>
  atom<string | null>(null)
)

// Shared by both Option Volume and Option Open Interest — each entry can switch
// between its time-series chart and its leaderboard table.
export type DataWidgetOptionsSubView = 'chart' | 'leaderboard'

export interface DataWidgetOptionVolumeSettings {
  view: DataWidgetOptionsSubView
  // Chart series measure, also reused as the leaderboard's `measure` param.
  measure: OptionsVolumeMeasure
  // Chart only — explicit lookback window; the bucket size (timeframe) is
  // auto-picked from the window's span (see resolveTimeframeForRange in
  // features/dashboard/components/widgets/options/analytics/common.tsx)
  // rather than being a separate user-facing control. null = default window
  // (ending "now"), same convention as the Amberdata from/to atoms.
  fromMs: number | null
  toMs: number | null
  streamOn: boolean
  // Leaderboard filters.
  optionType: OptionType
  groupBy: OptionsVolumeGroupBy
}

export const DATA_WIDGET_OPTION_VOLUME_SETTINGS_DEFAULT: DataWidgetOptionVolumeSettings = {
  view: 'chart',
  measure: OPTIONS_ANALYTICS_DEFAULT_MEASURE,
  fromMs: null,
  toMs: null,
  streamOn: true,
  optionType: OptionType.OPTION_TYPE_UNSPECIFIED,
  groupBy: OptionsVolumeGroupBy.OPTIONS_VOLUME_GROUP_BY_EXPIRY,
}

export const dataWidgetOptionVolumeSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetOptionVolumeSettings>(DATA_WIDGET_OPTION_VOLUME_SETTINGS_DEFAULT)
)

export interface DataWidgetOptionOpenInterestSettings {
  view: DataWidgetOptionsSubView
  // Chart series measure, also reused as the leaderboard's `measure` param.
  measure: OptionsVolumeMeasure
  // Chart only.
  timeframe: number
  // Chart only — bucket count, server max 1500.
  limit: number
  // Leaderboard filters.
  optionType: OptionType
  rankMode: OptionsOiRankMode
}

export const DATA_WIDGET_OPTION_OPEN_INTEREST_SETTINGS_DEFAULT: DataWidgetOptionOpenInterestSettings =
  {
    view: 'chart',
    measure: OPTIONS_ANALYTICS_DEFAULT_MEASURE,
    timeframe: Math.max(
      OPTIONS_ANALYTICS_DEFAULT_TIMEFRAME,
      OPTIONS_OI_SERIES_MIN_TIMEFRAME_SECONDS
    ),
    limit: OPTIONS_OI_SERIES_DEFAULT_LIMIT,
    optionType: OptionType.OPTION_TYPE_UNSPECIFIED,
    rankMode: OptionsOiRankMode.OPTIONS_OI_RANK_MODE_OI,
  }

export const dataWidgetOptionOpenInterestSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetOptionOpenInterestSettings>(DATA_WIDGET_OPTION_OPEN_INTEREST_SETTINGS_DEFAULT)
)

export const dataWidgetOptionTradesSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<OptionsAnalyticsTradesSettings>(OPTIONS_ANALYTICS_TRADES_SETTINGS_DEFAULT)
)

export interface DataWidgetOptionStrikeFlowSettings {
  measure: OptionsVolumeMeasure
  // Explicit lookback window — the bucket size (timeframe) is auto-picked from
  // the window's span (see resolveTimeframeForRange), same as Option Volume
  // above. null = default window (ending "now").
  fromMs: number | null
  toMs: number | null
  /** Absent = every expiry (the request omits `expiry_ms`). */
  expiryMs?: string
  /** True cumulatively sums each strike's flow left-to-right across buckets
   * instead of showing each bucket's own isolated buy/sell. */
  aggregate?: boolean
}

export const DATA_WIDGET_OPTION_STRIKE_FLOW_SETTINGS_DEFAULT: DataWidgetOptionStrikeFlowSettings = {
  measure: OPTIONS_ANALYTICS_DEFAULT_MEASURE,
  fromMs: null,
  toMs: null,
  expiryMs: undefined,
  // Isolated mode reads as noise for most strike ranges (each bucket's own
  // buy/sell), while aggregate (cumulative left-to-right) is what most people
  // actually want to see by default — flip the default, not just the option order.
  aggregate: true,
}

export const dataWidgetOptionStrikeFlowSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<DataWidgetOptionStrikeFlowSettings>(DATA_WIDGET_OPTION_STRIKE_FLOW_SETTINGS_DEFAULT)
)

// ENG-738 §7.1 — four snapshot-only derived panels (Implied Move, IV Percentile,
// Max Pain, Greeks by Expiry), toggled within one 'structure' mode instead of
// four more top-level ModeTabs entries.
export type OptionsAnalyticsStructureSubMode = 'impliedMove' | 'ivPercentile' | 'maxPain' | 'greeks'

export interface OptionsAnalyticsStructureSettings {
  subMode: OptionsAnalyticsStructureSubMode
}

export const OPTIONS_ANALYTICS_STRUCTURE_SETTINGS_DEFAULT: OptionsAnalyticsStructureSettings = {
  subMode: 'impliedMove',
}

export const dataWidgetOptionStructureSettingsAtomFamily = atomFamily((widgetId: string) =>
  atom<OptionsAnalyticsStructureSettings>(OPTIONS_ANALYTICS_STRUCTURE_SETTINGS_DEFAULT)
)

type WidgetDuplicateHandler = (sourceId: string, newId: string, pageId: string) => void

function duplicatePositions(sourceId: string, newId: string) {
  const expanded = store.get(positionsHistoryExpandedByInstanceAtom(sourceId))
  store.set(positionsHistoryExpandedByInstanceAtom(newId), expanded)
  store.set(positionsRestoredAtomFamily(newId), true)
  // Normalize in case the source were ever left mid-transition (defensive —
  // toggleHistoryExpanded always keeps the two in sync) — a section tab
  // copied into a widget that isn't expanded would otherwise blank the panel.
  store.set(
    positionTableTabByInstanceAtom(newId),
    normalizePositionsTab(store.get(positionTableTabByInstanceAtom(sourceId)), expanded)
  )
}

function duplicateOrderBook(sourceId: string, newId: string) {
  store.set(
    contractMultiplierModeAtomFamily(newId),
    store.get(contractMultiplierModeAtomFamily(sourceId))
  )
  store.set(
    orderbookShowBookPressureAtomFamily(newId),
    store.get(orderbookShowBookPressureAtomFamily(sourceId))
  )
  store.set(quantityDisplayAtomFamily(newId), store.get(quantityDisplayAtomFamily(sourceId)))
  store.set(
    orderbookDecimalDisplayAtomFamily(newId),
    store.get(orderbookDecimalDisplayAtomFamily(sourceId))
  )
  store.set(orderbookMaxRowsAtomFamily(newId), store.get(orderbookMaxRowsAtomFamily(sourceId)))
  store.set(
    orderbookShowVolumeDeltaAtomFamily(newId),
    store.get(orderbookShowVolumeDeltaAtomFamily(sourceId))
  )
  store.set(
    orderbookShowGaugesAtomFamily(newId),
    store.get(orderbookShowGaugesAtomFamily(sourceId))
  )
  store.set(
    orderbookShowFooterStatsAtomFamily(newId),
    store.get(orderbookShowFooterStatsAtomFamily(sourceId))
  )
}

function duplicateTrades(sourceId: string, newId: string) {
  store.set(
    contractMultiplierModeAtomFamily(newId),
    store.get(contractMultiplierModeAtomFamily(sourceId))
  )
  store.set(quantityDisplayAtomFamily(newId), store.get(quantityDisplayAtomFamily(sourceId)))
  store.set(tradesAmountDenomAtomFamily(newId), store.get(tradesAmountDenomAtomFamily(sourceId)))
  store.set(tradesSideFilterAtomFamily(newId), store.get(tradesSideFilterAtomFamily(sourceId)))
  store.set(tradesTimeDisplayAtomFamily(newId), store.get(tradesTimeDisplayAtomFamily(sourceId)))
  store.set(
    tradesAggregationSecondsAtomFamily(newId),
    store.get(tradesAggregationSecondsAtomFamily(sourceId))
  )
  store.set(
    tradesAggregationPriceModeAtomFamily(newId),
    store.get(tradesAggregationPriceModeAtomFamily(sourceId))
  )
  store.set(tradesBlockFilterAtomFamily(newId), store.get(tradesBlockFilterAtomFamily(sourceId)))
  store.set(tradesMaxCountAtomFamily(newId), store.get(tradesMaxCountAtomFamily(sourceId)))
  store.set(
    tradesRollingWindowAtomFamily(newId),
    store.get(tradesRollingWindowAtomFamily(sourceId))
  )
}

function duplicatePriceChart(sourceId: string, newId: string, pageId: string) {
  store.set(
    drawingPanelVisibleAtomFamily(newId),
    store.get(drawingPanelVisibleAtomFamily(sourceId))
  )

  const sourceKey = chartWidgetScopeKey(pageId, sourceId)
  const newKey = chartWidgetScopeKey(pageId, newId)
  const sourceColor: LinkingColor = store.get(linkingColorsAtomFamily(pageId))[sourceId] ?? 'mint'

  // Copy the *resolved* value, not the raw atom — if the source widget hasn't
  // been individually touched yet, its own atom is `undefined`, and copying
  // that verbatim would leave both source and duplicate reading (and
  // re-diverging via) the same legacy shared fallback instead of giving the
  // duplicate an independent snapshot.
  const resolvedChartOption =
    store.get(chartOptionAtomFamily(sourceKey)) ??
    resolveLegacyChartOption(pageId, sourceId, sourceColor) ??
    DEFAULT_GROUP_STATE.chartOption
  store.set(chartOptionAtomFamily(newKey), structuredClone(resolvedChartOption))

  const sourceTimeframeState = store.get(chartTabTimeframeAtomFamily(sourceKey))
  const sourceWatchList =
    sourceColor === 'unlinked'
      ? store.get(unlinkedCardStateAtomFamily(pageId))[sourceId]?.watchList
      : store.get(linkingGroupStateAtomFamily(pageId))[sourceColor]?.watchList
  const resolvedPerTab: Record<string, number> = {}
  for (const tab of sourceWatchList?.list ?? []) {
    resolvedPerTab[tab.id] =
      sourceTimeframeState?.perTab[tab.id] ??
      sourceTimeframeState?.default ??
      resolveLegacyTabTimeframe(pageId, sourceId, sourceColor, tab.id) ??
      tab.timeframe ??
      3600
  }
  store.set(chartTabTimeframeAtomFamily(newKey), {
    default: sourceTimeframeState?.default,
    perTab: resolvedPerTab,
  })
}

function duplicateEconomicCalendar(sourceId: string, newId: string) {
  store.set(calendarConfigAtomFamily(newId), store.get(calendarConfigAtomFamily(sourceId)))
}

function duplicateWatchlist(sourceId: string, newId: string) {
  store.set(watchlistActiveIdAtomFamily(newId), store.get(watchlistActiveIdAtomFamily(sourceId)))
  store.set(
    watchlistActiveIdReadyAtomFamily(newId),
    store.get(watchlistActiveIdReadyAtomFamily(sourceId))
  )
  store.set(
    watchlistColumnVisibilityAtomFamily(newId),
    store.get(watchlistColumnVisibilityAtomFamily(sourceId))
  )
}

function duplicateData(sourceId: string, newId: string) {
  store.set(dataWidgetContentAtomFamily(newId), store.get(dataWidgetContentAtomFamily(sourceId)))
  store.set(
    dataWidgetOptionExchangeAtomFamily(newId),
    store.get(dataWidgetOptionExchangeAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionUnderlyingAtomFamily(newId),
    store.get(dataWidgetOptionUnderlyingAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionVolumeSettingsAtomFamily(newId),
    store.get(dataWidgetOptionVolumeSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionOpenInterestSettingsAtomFamily(newId),
    store.get(dataWidgetOptionOpenInterestSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionTradesSettingsAtomFamily(newId),
    store.get(dataWidgetOptionTradesSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionStrikeFlowSettingsAtomFamily(newId),
    store.get(dataWidgetOptionStrikeFlowSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetOptionStructureSettingsAtomFamily(newId),
    store.get(dataWidgetOptionStructureSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetRangeDaysAtomFamily(newId),
    store.get(dataWidgetRangeDaysAtomFamily(sourceId))
  )
  store.set(dataWidgetDate1AtomFamily(newId), store.get(dataWidgetDate1AtomFamily(sourceId)))
  store.set(dataWidgetDate2AtomFamily(newId), store.get(dataWidgetDate2AtomFamily(sourceId)))
  store.set(dataWidgetFromDateAtomFamily(newId), store.get(dataWidgetFromDateAtomFamily(sourceId)))
  store.set(dataWidgetToDateAtomFamily(newId), store.get(dataWidgetToDateAtomFamily(sourceId)))
  store.set(
    dataWidgetCompareModeAtomFamily(newId),
    store.get(dataWidgetCompareModeAtomFamily(sourceId))
  )
  store.set(
    dataWidgetCompareSymbolAtomFamily(newId),
    store.get(dataWidgetCompareSymbolAtomFamily(sourceId))
  )
  store.set(
    dataWidgetSkewDeltaAtomFamily(newId),
    store.get(dataWidgetSkewDeltaAtomFamily(sourceId))
  )
  store.set(
    dataWidgetDeltaSurfaceViewModeAtomFamily(newId),
    store.get(dataWidgetDeltaSurfaceViewModeAtomFamily(sourceId))
  )
  store.set(
    dataWidgetIvOverTimeSettingsAtomFamily(newId),
    store.get(dataWidgetIvOverTimeSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetEtfFlowSettingsAtomFamily(newId),
    store.get(dataWidgetEtfFlowSettingsAtomFamily(sourceId))
  )
  store.set(
    dataWidgetFedwatchSettingsAtomFamily(newId),
    store.get(dataWidgetFedwatchSettingsAtomFamily(sourceId))
  )
}

function duplicateNewsfeed(sourceId: string, newId: string) {
  store.set(newsSearchQueryAtomFamily(newId), store.get(newsSearchQueryAtomFamily(sourceId)))
  store.set(newsTimestampModeAtomFamily(newId), store.get(newsTimestampModeAtomFamily(sourceId)))
  store.set(
    newsColumnVisibilityAtomFamily(newId),
    store.get(newsColumnVisibilityAtomFamily(sourceId))
  )
  store.set(newsTagFilterAtomFamily(newId), store.get(newsTagFilterAtomFamily(sourceId)))
  store.set(
    newsMacroEventFilterAtomFamily(newId),
    store.get(newsMacroEventFilterAtomFamily(sourceId))
  )
  store.set(newsCategoryFilterAtomFamily(newId), store.get(newsCategoryFilterAtomFamily(sourceId)))
  store.set(newsCategoryTabsAtomFamily(newId), store.get(newsCategoryTabsAtomFamily(sourceId)))
  store.set(newsSourceFilterAtomFamily(newId), store.get(newsSourceFilterAtomFamily(sourceId)))
}

function duplicateOneTapBuy(sourceId: string, newId: string) {
  store.set(oneTapBuyButtonsAtomFamily(newId), store.get(oneTapBuyButtonsAtomFamily(sourceId)))
}

function duplicateSpreader(sourceId: string, newId: string) {
  store.set(spreaderStateAtomFamily(newId), store.get(spreaderStateAtomFamily(sourceId)))
}

function duplicateAccountCards(sourceId: string, newId: string) {
  store.set(
    accountCardsSelectedAccountIdAtomFamily(newId),
    store.get(accountCardsSelectedAccountIdAtomFamily(sourceId))
  )
}

const DUPLICATE_HANDLERS: Partial<Record<ComponentKey, WidgetDuplicateHandler>> = {
  positions: duplicatePositions,
  'order-book': duplicateOrderBook,
  trades: duplicateTrades,
  'price-chart': duplicatePriceChart,
  'economic-calendar': duplicateEconomicCalendar,
  watchlist: duplicateWatchlist,
  data: duplicateData,
  newsfeed: duplicateNewsfeed,
  'account-cards': duplicateAccountCards,
  'one-tap-buy': duplicateOneTapBuy,
  spreader: duplicateSpreader,
}

type WidgetConfigBuilder = (
  sourceId: string,
  pageId: string
) => Record<string, string> | Promise<Record<string, string>>

function buildOrderBookConfig(sourceId: string): Record<string, string> {
  return {
    contractMultiplierMode: store.get(contractMultiplierModeAtomFamily(sourceId)),
    quantityDisplay: store.get(quantityDisplayAtomFamily(sourceId)),
    decimalDisplay: store.get(orderbookDecimalDisplayAtomFamily(sourceId)),
    maxRows: String(store.get(orderbookMaxRowsAtomFamily(sourceId))),
    showBookPressure: String(store.get(orderbookShowBookPressureAtomFamily(sourceId))),
    showVolumeDelta: String(store.get(orderbookShowVolumeDeltaAtomFamily(sourceId))),
    showGauges: String(store.get(orderbookShowGaugesAtomFamily(sourceId))),
    showFooterStats: String(store.get(orderbookShowFooterStatsAtomFamily(sourceId))),
  }
}

function buildTradesConfig(sourceId: string): Record<string, string> {
  return {
    contractMultiplierMode: store.get(contractMultiplierModeAtomFamily(sourceId)),
    quantityDisplay: store.get(quantityDisplayAtomFamily(sourceId)),
    amountDenom: store.get(tradesAmountDenomAtomFamily(sourceId)),
    sideFilter: store.get(tradesSideFilterAtomFamily(sourceId)),
    timeDisplay: store.get(tradesTimeDisplayAtomFamily(sourceId)),
    aggregationSeconds: String(store.get(tradesAggregationSecondsAtomFamily(sourceId))),
    aggregationPriceMode: store.get(tradesAggregationPriceModeAtomFamily(sourceId)),
    blockFilter: store.get(tradesBlockFilterAtomFamily(sourceId)),
    maxCount: String(store.get(tradesMaxCountAtomFamily(sourceId))),
    rollingWindow: String(store.get(tradesRollingWindowAtomFamily(sourceId))),
  }
}

// Exported for widget-config-restore.test.ts's serialize -> restore round-trip check; every
// other caller stays internal to this module.
export async function buildPriceChartConfig(
  sourceId: string,
  pageId: string
): Promise<Record<string, string>> {
  const scopeKey = chartWidgetScopeKey(pageId, sourceId)
  const sourceColor: LinkingColor = store.get(linkingColorsAtomFamily(pageId))[sourceId] ?? 'mint'
  const resolvedChartOption =
    store.get(chartOptionAtomFamily(scopeKey)) ??
    resolveLegacyChartOption(pageId, sourceId, sourceColor) ??
    DEFAULT_GROUP_STATE.chartOption
  const resolvedTimeframeState = store.get(chartTabTimeframeAtomFamily(scopeKey))
  const sourceWatchList =
    sourceColor === 'unlinked'
      ? store.get(unlinkedCardStateAtomFamily(pageId))[sourceId]?.watchList
      : store.get(linkingGroupStateAtomFamily(pageId))[sourceColor]?.watchList
  const focusedTab = sourceWatchList?.list[sourceWatchList.selectedIndex]
  const resolvedTimeframe =
    resolvedTimeframeState?.default ??
    (focusedTab
      ? resolveLegacyTabTimeframe(pageId, sourceId, sourceColor, focusedTab.id)
      : undefined) ??
    focusedTab?.timeframe ??
    3600

  const config: Record<string, string> = {
    drawingPanelVisible: String(store.get(drawingPanelVisibleAtomFamily(sourceId))),
    timeframe: String(resolvedTimeframe),
    scale: resolvedChartOption.scale,
  }
  // Both regular and Percept chart types share one wire field — the restore
  // path tells them apart by whether the string parses as a number.
  if (resolvedChartOption.perceptChartType !== undefined) {
    config.chartType = resolvedChartOption.perceptChartType
  } else if (resolvedChartOption.chartType !== undefined) {
    config.chartType = String(resolvedChartOption.chartType)
  }
  if (resolvedChartOption.autoScale !== undefined) {
    config.autoScale = String(resolvedChartOption.autoScale)
  }
  if (resolvedChartOption.showPositionLine !== undefined) {
    config.showPositionLine = String(resolvedChartOption.showPositionLine)
  }
  if (resolvedChartOption.showLiquidationLine !== undefined) {
    config.showLiquidationLine = String(resolvedChartOption.showLiquidationLine)
  }
  if (resolvedChartOption.showBuySellTriangles !== undefined) {
    config.showBuySellTriangles = String(resolvedChartOption.showBuySellTriangles)
  }
  if (resolvedChartOption.showBuySellButtons !== undefined) {
    config.showBuySellButtons = String(resolvedChartOption.showBuySellButtons)
  }
  if (resolvedChartOption.showOpenOrders !== undefined) {
    config.showOpenOrders = String(resolvedChartOption.showOpenOrders)
  }
  if (resolvedChartOption.showIndex !== undefined) {
    config.showIndex = String(resolvedChartOption.showIndex)
  }
  if (resolvedChartOption.showTopBar !== undefined) {
    config.showTopBar = String(resolvedChartOption.showTopBar)
  }
  if (resolvedChartOption.showHighLow !== undefined) {
    config.showHighLow = String(resolvedChartOption.showHighLow)
  }
  if (resolvedChartOption.perceptChartSettings) {
    config.perceptChartSettings = JSON.stringify(resolvedChartOption.perceptChartSettings)
  }
  if (resolvedChartOption.volumeDeltaProfileEnabled !== undefined) {
    config.volumeDeltaProfileEnabled = String(resolvedChartOption.volumeDeltaProfileEnabled)
  }
  if (resolvedChartOption.liquidationProfileEnabled !== undefined) {
    config.liquidationProfileEnabled = String(resolvedChartOption.liquidationProfileEnabled)
  }
  if (resolvedChartOption.openInterestProfileEnabled !== undefined) {
    config.openInterestProfileEnabled = String(resolvedChartOption.openInterestProfileEnabled)
  }
  if (resolvedChartOption.volumeDeltaProfileUnit !== undefined) {
    config.volumeDeltaProfileUnit = resolvedChartOption.volumeDeltaProfileUnit
  }
  if (resolvedChartOption.liquidationProfileUnit !== undefined) {
    config.liquidationProfileUnit = resolvedChartOption.liquidationProfileUnit
  }
  if (resolvedChartOption.openInterestProfileUnit !== undefined) {
    config.openInterestProfileUnit = resolvedChartOption.openInterestProfileUnit
  }
  if (resolvedChartOption.visibleRangeProfileUnit !== undefined) {
    config.visibleRangeProfileUnit = resolvedChartOption.visibleRangeProfileUnit
  }
  if (resolvedChartOption.liquidationLevelProfileEnabled !== undefined) {
    config.liquidationLevelProfileEnabled = String(
      resolvedChartOption.liquidationLevelProfileEnabled
    )
  }
  if (resolvedChartOption.stopLevelProfileEnabled !== undefined) {
    config.stopLevelProfileEnabled = String(resolvedChartOption.stopLevelProfileEnabled)
  }
  if (resolvedChartOption.liquidationLevelProfileMeasure !== undefined) {
    config.liquidationLevelProfileMeasure = resolvedChartOption.liquidationLevelProfileMeasure
  }
  if (resolvedChartOption.stopLevelProfileMeasure !== undefined) {
    config.stopLevelProfileMeasure = resolvedChartOption.stopLevelProfileMeasure
  }
  if (resolvedChartOption.volumeDeltaProfileShowLabels !== undefined) {
    config.volumeDeltaProfileShowLabels = String(resolvedChartOption.volumeDeltaProfileShowLabels)
  }
  if (resolvedChartOption.liquidationProfileShowLabels !== undefined) {
    config.liquidationProfileShowLabels = String(resolvedChartOption.liquidationProfileShowLabels)
  }
  if (resolvedChartOption.openInterestProfileShowLabels !== undefined) {
    config.openInterestProfileShowLabels = String(resolvedChartOption.openInterestProfileShowLabels)
  }
  if (resolvedChartOption.liquidationLevelProfileShowLabels !== undefined) {
    config.liquidationLevelProfileShowLabels = String(
      resolvedChartOption.liquidationLevelProfileShowLabels
    )
  }
  if (resolvedChartOption.stopLevelProfileShowLabels !== undefined) {
    config.stopLevelProfileShowLabels = String(resolvedChartOption.stopLevelProfileShowLabels)
  }
  if (resolvedChartOption.orderbookHeatmapEnabled !== undefined) {
    config.orderbookHeatmapEnabled = String(resolvedChartOption.orderbookHeatmapEnabled)
  }
  if (resolvedChartOption.orderbookHeatmapRange !== undefined) {
    config.orderbookHeatmapRange = String(resolvedChartOption.orderbookHeatmapRange)
  }
  if (resolvedChartOption.orderbookHeatmapGrouping !== undefined) {
    config.orderbookHeatmapGrouping = String(resolvedChartOption.orderbookHeatmapGrouping)
  }
  if (resolvedChartOption.orderbookHeatmapMinSize !== undefined) {
    config.orderbookHeatmapMinSize = String(resolvedChartOption.orderbookHeatmapMinSize)
  }
  if (resolvedChartOption.orderbookHeatmapMaxSize !== undefined) {
    config.orderbookHeatmapMaxSize = String(resolvedChartOption.orderbookHeatmapMaxSize)
  }

  // Drawings/indicators: best-effort. A compression failure shouldn't fail
  // the whole duplicate — the scalar settings above still get sent.
  if (resolvedChartOption.content) {
    try {
      config.content = await compressAndZipChartConfig(JSON.parse(resolvedChartOption.content))
    } catch (err) {
      console.error('[buildPriceChartConfig] failed to compress duplicate chart content', err)
    }
  }

  if (resolvedChartOption.perceptContent) {
    try {
      config.perceptContent = await compressAndZipChartConfig(
        JSON.parse(resolvedChartOption.perceptContent)
      )
    } catch (err) {
      console.error('[buildPriceChartConfig] failed to compress duplicate Percept content', err)
    }
  }

  return config
}

function buildEconomicCalendarConfig(sourceId: string): Record<string, string> {
  return { calendarConfig: JSON.stringify(store.get(calendarConfigAtomFamily(sourceId))) }
}

function buildWatchlistConfig(sourceId: string): Record<string, string> {
  return {
    activeWatchlistId: store.get(watchlistActiveIdAtomFamily(sourceId)) ?? '',
    columnVisibility: JSON.stringify(store.get(watchlistColumnVisibilityAtomFamily(sourceId))),
  }
}

function buildDataConfig(sourceId: string): Record<string, string> {
  const content = store.get(dataWidgetContentAtomFamily(sourceId))
  return {
    kind: content.kind,
    // Always included — for option kinds this is `previousAnalyticsType`, kept so
    // restoring an option-kind snapshot can still reconstruct it (see
    // restoreDataWidget).
    analyticsType: String(
      content.kind === 'amberdata' ? content.analyticsType : content.previousAnalyticsType
    ),
    rangeDays: store.get(dataWidgetRangeDaysAtomFamily(sourceId)),
    optionExchange: String(store.get(dataWidgetOptionExchangeAtomFamily(sourceId))),
    optionUnderlying: store.get(dataWidgetOptionUnderlyingAtomFamily(sourceId)) ?? '',
    optionVolumeSettings: JSON.stringify(
      store.get(dataWidgetOptionVolumeSettingsAtomFamily(sourceId))
    ),
    optionOpenInterestSettings: JSON.stringify(
      store.get(dataWidgetOptionOpenInterestSettingsAtomFamily(sourceId))
    ),
    optionTradesSettings: JSON.stringify(
      store.get(dataWidgetOptionTradesSettingsAtomFamily(sourceId))
    ),
    optionStrikeFlowSettings: JSON.stringify(
      store.get(dataWidgetOptionStrikeFlowSettingsAtomFamily(sourceId))
    ),
    optionStructureSettings: JSON.stringify(
      store.get(dataWidgetOptionStructureSettingsAtomFamily(sourceId))
    ),
    skewDelta: store.get(dataWidgetSkewDeltaAtomFamily(sourceId)),
    deltaSurfaceViewMode: store.get(dataWidgetDeltaSurfaceViewModeAtomFamily(sourceId)),
    ivOverTimeSettings: JSON.stringify(store.get(dataWidgetIvOverTimeSettingsAtomFamily(sourceId))),
    etfFlowSettings: JSON.stringify(store.get(dataWidgetEtfFlowSettingsAtomFamily(sourceId))),
    fedwatchSettings: JSON.stringify(store.get(dataWidgetFedwatchSettingsAtomFamily(sourceId))),
  }
}

function buildNewsfeedConfig(sourceId: string): Record<string, string> {
  return {
    timestampMode: store.get(newsTimestampModeAtomFamily(sourceId)),
    tagFilter: store.get(newsTagFilterAtomFamily(sourceId)),
    macroEventFilter: store.get(newsMacroEventFilterAtomFamily(sourceId)),
    columnVisibility: JSON.stringify(store.get(newsColumnVisibilityAtomFamily(sourceId))),
    categoryFilter: store.get(newsCategoryFilterAtomFamily(sourceId)),
    categoryTabs: JSON.stringify(store.get(newsCategoryTabsAtomFamily(sourceId))),
    sourceFilter: JSON.stringify(store.get(newsSourceFilterAtomFamily(sourceId))),
    // Stamp the current version so a widget duplicated from an already-migrated
    // source doesn't look "unversioned" and re-trigger the tree_news migration
    // in widget-config-restore.ts on its own next restore.
    sourceFilterVersion: String(NEWS_SOURCE_FILTER_VERSION),
  }
}

function buildOneTapBuyConfig(sourceId: string): Record<string, string> {
  return { buttons: JSON.stringify(store.get(oneTapBuyButtonsAtomFamily(sourceId))) }
}

function buildAccountCardsConfig(sourceId: string): Record<string, string> {
  const selectedAccountIds = store.get(accountCardsSelectedAccountIdAtomFamily(sourceId))
  return {
    accountId: selectedAccountIds === null ? ALL_ACCOUNTS_ID : JSON.stringify(selectedAccountIds),
  }
}

function buildSpreaderConfig(sourceId: string): Record<string, string> {
  return {
    spreaderState: JSON.stringify(store.get(spreaderStateAtomFamily(sourceId))),
  }
}

function buildPositionsConfig(sourceId: string): Record<string, string> {
  return {
    historyTabsExpanded: String(store.get(positionsHistoryExpandedByInstanceAtom(sourceId))),
  }
}

const CONFIG_BUILDERS: Partial<Record<ComponentKey, WidgetConfigBuilder>> = {
  'order-book': buildOrderBookConfig,
  trades: buildTradesConfig,
  'price-chart': buildPriceChartConfig,
  'economic-calendar': buildEconomicCalendarConfig,
  watchlist: buildWatchlistConfig,
  data: buildDataConfig,
  newsfeed: buildNewsfeedConfig,
  positions: buildPositionsConfig,
  'account-cards': buildAccountCardsConfig,
  'one-tap-buy': buildOneTapBuyConfig,
  spreader: buildSpreaderConfig,
}

/**
 * Builds the server-config payload (same shape `useWidgetConfigSync` writes and
 * `widget-config-restore` reads back) for the settings `duplicateWidgetAtoms` copies
 * locally, so the duplicated widget's settings survive a page reload.
 */
export async function buildDuplicateWidgetConfig(
  componentKey: ComponentKey,
  sourceId: string,
  pageId: string,
  sourceColor: LinkingColor = store.get(linkingColorsAtomFamily(pageId))[sourceId] ?? 'mint'
): Promise<Record<string, string>> {
  return {
    ...((await CONFIG_BUILDERS[componentKey]?.(sourceId, pageId)) ?? {}),
    group: sourceColor,
  }
}

/**
 * Copies the per-widget setting atoms (column visibility, filters, tabs, etc.)
 * relevant to `componentKey` from one widget instance to another. Used when
 * duplicating a widget so the new card starts with the same settings as the
 * source. Does not copy ephemeral state (e.g. row selection).
 */
export function duplicateWidgetAtoms(
  componentKey: ComponentKey,
  sourceId: string,
  newId: string,
  pageId: string
) {
  DUPLICATE_HANDLERS[componentKey]?.(sourceId, newId, pageId)
}

export function cleanupWidgetAtoms(widgetId: string, pageId: string) {
  const scopeKey = chartWidgetScopeKey(pageId, widgetId)
  cleanupUnlinkedOptionRows(pageId, widgetId)
  // RESET clears the atomWithStorage's persisted localStorage value —
  // .remove() below only evicts the in-memory atomFamily cache entry, which
  // alone would leave the old value in localStorage to potentially resurrect
  // if a widget ID is ever reused (possible with the legacy counter-based ID
  // scheme).
  store.set(chartOptionAtomFamily(scopeKey), RESET)
  store.set(chartTabTimeframeAtomFamily(scopeKey), RESET)
  chartOptionAtomFamily.remove(scopeKey)
  chartTabTimeframeAtomFamily.remove(scopeKey)
  selectedPositionsByInstanceAtom.remove(widgetId)
  positionTableTabByInstanceAtom.remove(widgetId)
  positionsHistoryExpandedByInstanceAtom.remove(widgetId)
  positionsRestoredAtomFamily.remove(widgetId)
  activeRowCountByInstanceAtom.remove(widgetId)
  filteredPositionCountByInstanceAtom.remove(widgetId)
  quantityDisplayAtomFamily.remove(widgetId)
  contractMultiplierModeAtomFamily.remove(widgetId)
  orderbookDecimalDisplayAtomFamily.remove(widgetId)
  tradesAmountDenomAtomFamily.remove(widgetId)
  tradesSideFilterAtomFamily.remove(widgetId)
  tradesTimeDisplayAtomFamily.remove(widgetId)
  tradesAggregationSecondsAtomFamily.remove(widgetId)
  tradesAggregationPriceModeAtomFamily.remove(widgetId)
  tradesBlockFilterAtomFamily.remove(widgetId)
  tradesMaxCountAtomFamily.remove(widgetId)
  tradesRollingWindowAtomFamily.remove(widgetId)
  orderbookMaxRowsAtomFamily.remove(widgetId)
  orderbookShowBookPressureAtomFamily.remove(widgetId)
  orderbookShowVolumeDeltaAtomFamily.remove(widgetId)
  orderbookShowGaugesAtomFamily.remove(widgetId)
  orderbookShowFooterStatsAtomFamily.remove(widgetId)
  accountCardsSelectedAccountIdAtomFamily.remove(widgetId)
  drawingPanelVisibleAtomFamily.remove(widgetId)
  optionChainViewVisibleAtomFamily.remove(widgetId)
  optionChainVisiblePreferenceAtomFamily.remove(widgetId)
  chartSelectedAccountIdAtomFamily.remove(widgetId)
  calendarConfigAtomFamily.remove(widgetId)
  watchlistActiveIdAtomFamily.remove(widgetId)
  watchlistActiveIdReadyAtomFamily.remove(widgetId)
  watchlistColumnVisibilityAtomFamily.remove(widgetId)
  dataWidgetContentAtomFamily.remove(widgetId)
  dataWidgetOptionExchangeAtomFamily.remove(widgetId)
  dataWidgetOptionUnderlyingAtomFamily.remove(widgetId)
  dataWidgetOptionVolumeSettingsAtomFamily.remove(widgetId)
  dataWidgetOptionOpenInterestSettingsAtomFamily.remove(widgetId)
  dataWidgetOptionTradesSettingsAtomFamily.remove(widgetId)
  dataWidgetOptionStrikeFlowSettingsAtomFamily.remove(widgetId)
  dataWidgetOptionStructureSettingsAtomFamily.remove(widgetId)
  dataWidgetRangeDaysAtomFamily.remove(widgetId)
  dataWidgetDate1AtomFamily.remove(widgetId)
  dataWidgetDate2AtomFamily.remove(widgetId)
  dataWidgetFromDateAtomFamily.remove(widgetId)
  dataWidgetToDateAtomFamily.remove(widgetId)
  dataWidgetCompareModeAtomFamily.remove(widgetId)
  dataWidgetCompareSymbolAtomFamily.remove(widgetId)
  dataWidgetSkewDeltaAtomFamily.remove(widgetId)
  dataWidgetDeltaSurfaceViewModeAtomFamily.remove(widgetId)
  dataWidgetIvOverTimeSettingsAtomFamily.remove(widgetId)
  dataWidgetEtfFlowSettingsAtomFamily.remove(widgetId)
  dataWidgetFedwatchSettingsAtomFamily.remove(widgetId)
  newsSearchQueryAtomFamily.remove(widgetId)
  newsCategoryFilterAtomFamily.remove(widgetId)
  newsCategoryTabsAtomFamily.remove(widgetId)
  newsTimestampModeAtomFamily.remove(widgetId)
  newsColumnVisibilityAtomFamily.remove(widgetId)
  newsTagFilterAtomFamily.remove(widgetId)
  newsMacroEventFilterAtomFamily.remove(widgetId)
  newsSourceFilterAtomFamily.remove(widgetId)
  oneTapBuyButtonsAtomFamily.remove(widgetId)
  spreaderStateAtomFamily.remove(widgetId)
  spreaderRestoredAtomFamily.remove(widgetId)
  riskSlidesFilterAtomFamily.remove(widgetId)
  riskSimulationResponseAtomFamily.remove(widgetId)
  simulatedPortfolioAtomFamily.remove(widgetId)
  riskSimulationTriggerAtomFamily.remove(widgetId)
  // RESET first, same reasoning as chartOptionAtomFamily above — .remove() alone
  // only evicts the in-memory atomFamily cache entry and would leave saved scenarios
  // in localStorage to resurrect if a widget ID is ever reused.
  store.set(savedRiskScenariosAtomFamily(widgetId), RESET)
  savedRiskScenariosAtomFamily.remove(widgetId)
}
